import { Injector, LOCALE_ID } from '@angular/core';
import { Validators } from '@angular/forms';
import { ColumnSizes, FieldSelectOption, FilterType } from 'components';
import { DateDisplayPipe } from 'projects/components/src/lib/shared/pipes/date-display/date-display.pipe';
import { Report } from '../../models/report';
import { ScheduledPaymentsReportService } from './scheduled-payments.service';

export const ScheduledPaymentsReport: Report = {
    id: 'scheduled-payments',
    label: 'Scheduled Payments Report',
    textCriteria: { 'Created Start Date': '', 'Created End Date': '', 'Next Payment Start Date': '', 'Next Payment End Date': '', 'Amount Types': '', 'Frequencies': '' },
    cleanFilterParams: { 'userIds': '' },
    getReportTitle: () => {
        return ScheduledPaymentsReport.label + ' ' + ScheduledPaymentsReport.textCriteria['Created Start Date'] + ' - ' + ScheduledPaymentsReport.textCriteria['Created End Date'];
    },
    getSearchCriteria: (injector: Injector) => [
        {
            title: '',
            controls: [
                {
                    label: 'Created Date Range',
                    name: 'createdDateRange',
                    type: 'dateRange',
                    class: 'form-span-12',
                    daterangeStartChanged: (event) => {
                        ScheduledPaymentsReport.textCriteria['Created Start Date'] = event.value.toLocaleDateString();
                    },
                    daterangeEndChanged: (event) => {
                        ScheduledPaymentsReport.textCriteria['Created End Date'] = event.value.toLocaleDateString();
                    }
                },
                {
                    label: '',
                    name: 'createdDateRangeStart',
                    type: 'noInput',
                    class: 'display-none',
                },
                {
                    label: '',
                    name: 'createdDateRangeEnd',
                    type: 'noInput',
                    class: 'display-none',
                },
                {
                    label: 'Next Payment Date Range',
                    name: 'nextDateRange',
                    type: 'dateRange',
                    class: 'form-span-12',
                    daterangeStartChanged: (event) => {
                        ScheduledPaymentsReport.textCriteria['Next Payment Start Date'] = event.value.toLocaleDateString();
                    },
                    daterangeEndChanged: (event) => {
                        ScheduledPaymentsReport.textCriteria['Next Payment End Date'] = event.value.toLocaleDateString();
                    }
                },
                {
                    label: '',
                    name: 'nextDateRangeStart',
                    type: 'noInput',
                    class: 'display-none',
                },
                {
                    label: '',
                    name: 'nextDateRangeEnd',
                    type: 'noInput',
                    class: 'display-none',
                },
                {
                    label: 'Frequency Types',
                    name: 'frequencies',
                    class: 'form-span-6',
                    type: 'select',
                    multiple: true,
                    options: [{ value: 1, label: 'Weekly' }, { value: 2, label: 'Monthly' }, { value: 3, label: 'Monthly - last day of month' }, { value: 4, label: 'Custom' }],
                    selectionChanged: (event) => {
                        ScheduledPaymentsReport.textCriteria['Frequency Types'] = event.source.triggerValue;
                    }
                },
                {
                    label: 'Amount Types',
                    name: 'amountTypes',
                    class: 'form-span-6',
                    type: 'select',
                    multiple: true,
                    options: [{ value: 1, label: 'Patient Balance' }, { value: 2, label: 'Custom Amount' }],
                    selectionChanged: (event) => {
                        ScheduledPaymentsReport.textCriteria['Amount Types'] = event.source.triggerValue;
                    },
                }
            ]
        }
    ],
    getGridConfiguration: (injector: Injector) => {
        const locale = injector.get(LOCALE_ID);
        const datePipe: DateDisplayPipe = new DateDisplayPipe(locale);
        const moneyFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
        return {
            service: injector.get<any>(ScheduledPaymentsReportService),
            columnDefinitions: [
                {
                    id: 'account_number',
                    displayKey: 'account_number',
                    headerText: 'Account Number',
                    sortable: true,
                    filterable: true,
                    displayOrder: 0,
                    columnSize: ColumnSizes.normal,
                    cellClickEvent: (data) => {
                        if (data.patientId !== '' && data.patientId !== null) {
                            window.open(`/accounts/detail/${data.patientId}`, '_blank');
                        }
                    },
                    link_underline_condition: (data) => {
                        return data.patientId !== null && data.patientId !== '';
                    }
                },
                {
                    id: 'first_name',
                    displayKey: 'first_name',
                    headerText: 'First Name',
                    sortable: true,
                    displayOrder: 1,
                    columnSize: ColumnSizes.normal,
                    filterable: true,
                    cellClickEvent: (data) => {
                        if (data.patientId !== '' && data.patientId !== null) {
                            window.open(`/accounts/detail/${data.patientId}`, '_blank');
                        }
                    },
                    link_underline_condition: (data) => {
                        return data.patientId !== null && data.patientId !== '';
                    }
                },
                {
                    id: 'last_name',
                    displayKey: 'last_name',
                    headerText: 'Last Name',
                    sortable: true,
                    displayOrder: 2,
                    columnSize: ColumnSizes.normal,
                    filterable: true,
                    cellClickEvent: (data) => {
                        if (data.patientId !== '' && data.patientId !== null) {
                            window.open(`/accounts/detail/${data.patientId}`, '_blank');
                        }
                    },
                    link_underline_condition: (data) => {
                        return data.patientId !== null && data.patientId !== '';
                    }
                },
                {
                    id: 'frequency',
                    displayKey: 'frequency',
                    headerText: 'Frequency',
                    sortable: true,
                    displayOrder: 3,
                    columnSize: ColumnSizes.wide,
                    filterable: true,
                },
                {
                    id: 'amount',
                    displayKey: 'amount',
                    headerText: 'Amount',
                    sortable: true,
                    displayOrder: 4,
                    columnSize: ColumnSizes.normal,
                    filterable: true,
                    sortColumnType: 'number',
                    filterConfig: {
                        filterType: FilterType.number
                    }
                },
                {
                    id: 'created_date',
                    displayKey: 'created_date',
                    headerText: 'Created Date',
                    displayOrder: 5,
                    columnSize: ColumnSizes.date,
                    sortable: true,
                    filterable: true,
                    filterConfig: {
                        filterType: FilterType.date,
                        filterKey: 'created_date'
                    },
                    formatter: (x) => {
                        return datePipe.transform(x)
                    },
                    sortColumnType: 'date'
                },
                {
                    id: 'payment_start_date',
                    displayKey: 'payment_start_date',
                    headerText: 'Payment Start Date',
                    displayOrder: 6,
                    columnSize: ColumnSizes.date,
                    filterConfig: {
                        filterType: FilterType.date,
                        filterKey: 'payment_start_date'
                    },
                    formatter: (x) => {
                        return datePipe.transform(x)
                    },
                    sortable: true,
                    filterable: true,
                    sortColumnType: 'date'
                },
                {
                    id: 'payment_end_date',
                    displayKey: 'payment_end_date',
                    headerText: 'Payment End Date',
                    displayOrder: 7,
                    columnSize: ColumnSizes.date,
                    sortable: true,
                    filterable: true,
                    filterConfig: {
                        filterType: FilterType.date,
                        filterKey: 'payment_end_date'
                    },
                    formatter: (x) => {
                        return datePipe.transform(x)
                    },
                    sortColumnType: 'date'
                },
                {
                    id: 'next_payment_date',
                    displayKey: 'next_payment_date',
                    headerText: 'Next Payment Date',
                    displayOrder: 8,
                    columnSize: ColumnSizes.date,
                    sortable: true,
                    filterable: true,
                    filterConfig: {
                        filterType: FilterType.date,
                        filterKey: 'next_payment_date'
                    },
                    formatter: (x) => {
                        return datePipe.transform(x)
                    },
                    sortColumnType: 'date'
                },
                {
                    id: 'last_payment_date',
                    displayKey: 'last_payment_date',
                    headerText: 'Last Payment Date',
                    displayOrder: 9,
                    columnSize: ColumnSizes.date,
                    sortable: true,
                    filterable: true,
                    filterConfig: {
                        filterType: FilterType.date,
                        filterKey: 'last_payment_date'
                    },
                    formatter: (x) => {
                        return datePipe.transform(x)
                    },
                    sortColumnType: 'date'
                },
            ]
        };
    }
};
